import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FormeoRenderer } from 'formeo';
import intakeFormService from '@/services/intake-form/intakeFormService';
import "formeo/dist/formeo.min.css";
import { PublicHeader } from '@/layouts/main/header-public';
import { toast } from 'sonner';
import { injectCheckboxOtherOptions, applyCheckboxRequiredAttributes } from './checkboxOther';
import Select from 'react-select';
import { createRoot } from 'react-dom/client';
import { getGlobalSelectClass } from '@/utils';

const restructureFormData = (formData, formFields) => {
    const structuredData = {
        intake_form_id: formData.intake_form_id,
        contact: {},
        matter: {},
    };

    let phoneNumbers = [];

    Object.keys(formData).forEach((key) => {
        const value = formData[key];

        const field = Object.values(formFields.fields).find(field => field.config && field.config.sys && field.config.sys.full_name === key);

        if (field) {
            const category = field.config.category; 

            // Handle custom fields based on category (e.g., contact.custom_fields)
            if (category && category.startsWith('contact.custom_fields')) {
                if (!structuredData.contact.custom_fields) {
                    structuredData.contact.custom_fields = {};
                }
                const customField = key.replace('contact.', ''); // Remove 'contact.' prefix
                structuredData.contact.custom_fields[customField] = value;
            } else if (category && category.startsWith('matter.custom_fields')) {
                if (!structuredData.matter.custom_fields) {
                    structuredData.matter.custom_fields = {};
                }
                const customField = key.replace('matter.', ''); // Remove 'matter.' prefix
                structuredData.matter.custom_fields[customField] = value;
            } else if (category && category.startsWith('contact.address')) {
                const addressField = key.replace('contact.address.', ''); // Remove 'contact.address.' prefix
                if (!structuredData.contact.address) {
                    structuredData.contact.address = {};
                }
                structuredData.contact.address[addressField] = value; // Add to the address object
            } else if (category && category === 'contact.phones') {
                // Handle phone_number fields (contact.number, contact.title)
                const phoneField = key.split('.')[1];

                let phoneObj = phoneNumbers[0];

                if (!phoneObj) {
                    phoneObj = {};
                    phoneNumbers.push(phoneObj);
                }

                phoneObj[phoneField] = value;

                console.log('phoneObj', phoneObj);
            } else {
                // Regular fields, just map them to contact or matter
                const fieldCategory = key.split('.')[0];
                const fieldName = key.split('.').slice(1).join('.');

                if (fieldCategory === 'contact') {
                    structuredData.contact[fieldName] = value;
                } else if (fieldCategory === 'matter') {
                    structuredData.matter[fieldName] = value;
                }
            }
        } else {
            console.warn(`Field not found for key: ${key}`);
        }
    });

    if (phoneNumbers.length > 0) {
        structuredData.contact.phone_number = phoneNumbers;
    }

    return structuredData;
};

const buildSelectOptionsSignature = (options) =>
    options.map((o) => `${o.value}::${o.label}`).join("|");

const enhanceMultiSelects = (container) => {
    if (!container) return;

    const selects = container.querySelectorAll('select[multiple]');

    selects.forEach((select) => {
        const options = Array.from(select.options).map((opt) => ({
            label: opt.text,
            value: opt.value,
        }));
        const optionsSig = buildSelectOptionsSignature(options);

        const prevSig = select.dataset.optionsSignature;
        const isEnhanced = select.dataset.enhanced === 'true';

        if (isEnhanced && prevSig !== optionsSig) {
            const reactSelectId = select.dataset.reactSelectId;
            if (reactSelectId) {
                const mount = select.parentNode?.querySelector(
                    `[data-react-select-for="${reactSelectId}"]`
                );
                if (mount) mount.remove();
            }
            delete select.dataset.enhanced;
        }

        if (select.dataset.enhanced === 'true') return;

        select.dataset.enhanced = 'true';
        select.dataset.optionsSignature = optionsSig;

        const reactSelectId =
            select.dataset.reactSelectId ||
            `rs_${Math.random().toString(36).slice(2, 9)}`;
        select.dataset.reactSelectId = reactSelectId;

        const mount = document.createElement('div');
        mount.className = 'react-select-wrapper';
        mount.dataset.reactSelectFor = reactSelectId;

        select.classList.add('hidden-native-select');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');

        select.parentNode.insertBefore(mount, select.nextSibling);

        const initialValue = Array.from(select.selectedOptions).map((o) => ({
            label: o.text,
            value: o.value,
        }));

        const root = createRoot(mount);

        root.render(
            <Select
                classNames={getGlobalSelectClass()}
                isMulti
                isDisabled={select.disabled}
                options={options}
                defaultValue={initialValue}
                className='input'
                classNamePrefix="react-select"
                placeholder="Select options..."
                onChange={(selected) => {
                    const values = (selected || []).map((s) => s.value);

                    Array.from(select.options).forEach((opt) => {
                        opt.selected = values.includes(opt.value);
                    });

                    select.dispatchEvent(new Event('change', { bubbles: true }));
                }}
            />
        );
    });
};

const IntakeFormEmbed = () => {
    const { id } = useParams();
    const [fetchedForm, setFetchedForm] = useState(null);
    const formContainerRef = useRef(null);
    const [renderer, setRenderer] = useState(null);
    const [submitFormLoading, setSubmitFormLoading] = useState(false)
    const [showThankYouMsg, setShowThankYouMsg] = useState(false)

    const fetchIntakeForm = async () => {
        try {
            const { data, status } = await intakeFormService.getSingleForm(id);
            if (status) {
                setFetchedForm(data);
            }
        } catch (error) {
            console.error("Error Fetching Intake Form", error);
        }
    };

    useEffect(() => {
        fetchIntakeForm();
    }, [id]);

    useEffect(() => {
        if (fetchedForm && fetchedForm.fields && formContainerRef.current) {
            const parsedFields = typeof fetchedForm.fields === "string" ? JSON.parse(fetchedForm.fields) : fetchedForm.fields;

            // console.log('Form container:', formContainerRef.current);
            // console.log('Fetched Form Fields:', parsedFields);

            // Initialize FormeoRenderer
            const rendererInstance = new FormeoRenderer({
                renderContainer: formContainerRef.current,
            });

            // Render the form
            rendererInstance.render(parsedFields);
            requestAnimationFrame(() => {
                injectCheckboxOtherOptions(formContainerRef.current, parsedFields);
                applyCheckboxRequiredAttributes(formContainerRef.current, parsedFields);
                enhanceMultiSelects(formContainerRef.current);
            });

            setRenderer(rendererInstance);
        }
    }, [fetchedForm]);

    // Handle form submission
    const handleSubmit = async () => {
        if (renderer) {
            setSubmitFormLoading(true)
            const formData = {};

            const inputs = formContainerRef.current.querySelectorAll("input, select, textarea");
            inputs.forEach((input) => {
                if (input.tagName === 'SELECT' && input.multiple) {
                    formData[input.name] = Array.from(input.selectedOptions).map(o => o.value);
                    return;
                }
                formData[input.name] = input.value;
            });
            formData.intake_form_id = id;
            const payload = restructureFormData(formData, JSON.parse(fetchedForm.fields))
            console.log('formData', payload)
            // setSubmitFormLoading(false)
            // return

            try {
                const { status } = await intakeFormService.postIntakeForm(payload);
                if (status) {
                    setShowThankYouMsg(true)
                    console.log("Form submitted successfully:");
                }
            } catch (error) {
                console.error("Error submitting form", error);
                toast.error('Oops! Something went wrong.', {
                    description: 'An error occurred while submitting the form.',
                });
            } finally {
                setSubmitFormLoading(false)
            }
        }
    };

    if (!fetchedForm) {
        return <div>Loading form...</div>;
    }


    return (
        <div className="w-full h-full">
            {showThankYouMsg ? (
                <div className='flex items-center justify-center h-[80vh]'>
                    <div className="card container-fixed max-w-3xl mx-auto mt-6 w-full text-center border-2 border-dashed my-5 p-5 h-[150px] flex items-center justify-center flex-col text-sm rounded-md transition border-success bg-success-light">
                        <p className="text-xl font-medium text-success-active">Form submitted successfully!</p>
                        <p className="mt-1 text-xs">Thank you! You may now close this window.</p>
                    </div>
                </div>
            ) : (
                <div className="card container-fixed mx-auto mt-6">
                    <div className="card-header">
                        <h3 className="card-title">{fetchedForm.name}</h3>
                    </div>
                    <div className="card-body">
                        <div ref={formContainerRef} className="formeo-renderer-container" />
                    </div>
                </div>
            )}

        </div>
    );
};

export { IntakeFormEmbed };

